/**
 * View Transitions — cross-document (MPA) navigation between listing and detail.
 *
 * Hooks into `pageswap` (old side) and `pagereveal` (new side) to set
 * `view-transition-name` / `view-transition-class` styles just before snapshot
 * capture. Must be enqueued in <head> as a classic (non-deferred) script so the
 * `pagereveal` listener is registered before the browser's first rendering
 * opportunity on the destination document. See `inc/enqueue.php`.
 *
 * The script is intentionally PREFIX-AGNOSTIC: it reads naming prefixes off
 * card data-attributes (`data-vt-image`, `data-vt-title`, `data-vt-date`) so
 * the same script serves news-listing-with-facets-26, post-listing-with-facets-26,
 * post-featured, and any future caller that follows the convention.
 *
 * Cards that ALSO declare `data-vt-set="..."` opt in to the staggered "other
 * cards" treatment used by the news listing; the JS namespaces the shared
 * classes under that set (e.g. `post-news-hero`, `post-news-other`,
 * `post-news-stagger-{0..7}`) so non-opted-in listings stay unaffected.
 */
(function () {
    'use strict';

    var STORAGE_KEY = 'coe-vt-post';
    var FORWARD = 'forward';
    var BACK = 'back';
    var reducedMotion = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

    function setVTName(el, name, classes) {
        // A view-transition-name must be unique document-wide at snapshot time;
        // a duplicate aborts the WHOLE transition (InvalidStateError → instant
        // nav, no morph). The same post can legitimately render in two blocks
        // on one page (news-featured-26 hero + the listing below), so steal the
        // name from any rival element before assigning it to the clicked card.
        document.querySelectorAll('[style*="view-transition-name"]').forEach(function (rival) {
            if (rival !== el && rival.style.viewTransitionName === name) {
                rival.style.viewTransitionName = '';
                rival.style.viewTransitionClass = '';
            }
        });
        el.style.viewTransitionName = name;
        if (classes) el.style.viewTransitionClass = classes;
        el.dataset.vtDynamic = '1';
    }

    function clearDynamic() {
        document.querySelectorAll('[data-vt-dynamic]').forEach(function (el) {
            el.style.viewTransitionName = '';
            el.style.viewTransitionClass = '';
            delete el.dataset.vtDynamic;
        });
    }

    function isVisible(el) {
        if (!el.offsetParent && getComputedStyle(el).position !== 'fixed') return false;
        var r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
    }

    /**
     * Set per-id `view-transition-name` on every card up-front. Preserves
     * legacy behavior for non-opted-in callers (post-featured, post-26): they
     * always relied on names being present on the OLD side at snapshot time,
     * regardless of which card the user clicked. No class assignment here —
     * shared classes are only added during pageswap/pagereveal for listings
     * that opt in via `data-vt-set`.
     */
    function preTagAllCards() {
        // Duplicate names abort every transition on the page (see setVTName),
        // and the same post can appear in two blocks (featured + listing).
        // First card in DOM order wins here; if the user clicks the OTHER
        // card, tagCardForMorph → setVTName steals the name back at
        // pageswap/pagereveal time, so the morph still originates from the
        // card that was actually clicked.
        var used = {};
        document.querySelectorAll('[style*="view-transition-name"]').forEach(function (el) {
            if (el.style.viewTransitionName) used[el.style.viewTransitionName] = true;
        });
        document.querySelectorAll('[data-post-id]').forEach(function (card) {
            var postId = card.dataset.postId;
            var imgWrap = card.querySelector('[data-vt-image]');
            if (imgWrap) {
                var img = imgWrap.tagName === 'IMG' ? imgWrap : imgWrap.querySelector('img');
                var imgName = imgWrap.dataset.vtImage + '-' + postId;
                if (img && !img.style.viewTransitionName && !used[imgName]) {
                    img.style.viewTransitionName = imgName;
                    used[imgName] = true;
                }
            }
            var title = card.querySelector('[data-vt-title]');
            if (title) {
                var titleName = title.dataset.vtTitle + '-' + postId;
                if (!title.style.viewTransitionName && !used[titleName]) {
                    title.style.viewTransitionName = titleName;
                    used[titleName] = true;
                }
            }
        });
    }

    function tagCardForMorph(card, postId) {
        var set = card.dataset.vtSet || null;
        var heroClass = set ? set + '-hero' : null;
        // Foreground class lifts title + person above the new-only overlay
        // group on the forward leg. Image stays at hero-only so the back-leg
        // overlay fade-out isn't occluded by the morphing image. See SCSS
        // comment at _content-news.scss `::view-transition-group(.post-news-hero-fg)`.
        var heroFgClass = heroClass ? heroClass + ' ' + heroClass + '-fg' : null;

        var imgWrap = card.querySelector('[data-vt-image]');
        if (imgWrap) {
            var img = imgWrap.tagName === 'IMG' ? imgWrap : imgWrap.querySelector('img');
            if (img) setVTName(img, imgWrap.dataset.vtImage + '-' + postId, heroClass);
        }
        var title = card.querySelector('[data-vt-title]');
        if (title) setVTName(title, title.dataset.vtTitle + '-' + postId, heroFgClass);

        // People — only when the family declares a set. Chip-row + compact-stack
        // are mutually exclusive at any breakpoint but both live in the DOM, so
        // pick the visible one per person to avoid duplicate VT-name collisions.
        if (set) {
            var seen = {};
            card.querySelectorAll('[data-vt-person]').forEach(function (el) {
                var pid = el.dataset.vtPerson;
                if (seen[pid] || !isVisible(el)) return;
                seen[pid] = true;
                setVTName(el, set + '-person-' + pid, heroFgClass);
            });
        }
    }

    function clearDirAttrs() {
        delete document.documentElement.dataset.vtDir;
        delete document.documentElement.dataset.vtSet;
    }

    /**
     * Temporarily lift the pre-tagged names from every card EXCEPT keepCard
     * while a choreographed transition runs on this (new) document. Pre-tagged
     * siblings otherwise become independent NEW-ONLY snapshot groups at
     * capture: they pop in on the UA default 0.25s fade instead of riding the
     * beat-3 root fade-in, and — appended to the pseudo-tree after the
     * morphing pair — they paint ABOVE the hero as it shrinks back into its
     * card. Stripping folds them back into the root snapshot (correct fade
     * timing, correct stacking). preTagAllCards() re-tags after the
     * transition finishes. Pass null to strip every card (forward leg: the
     * detail page's related-post cards are never morph targets on arrival).
     */
    function stripSiblingPreTags(keepCard) {
        document.querySelectorAll('[data-post-id]').forEach(function (card) {
            if (card === keepCard) return;
            card.querySelectorAll('[style*="view-transition-name"]').forEach(function (el) {
                if (el.dataset.vtDynamic) return; // belongs to the active morph
                el.style.viewTransitionName = '';
            });
        });
    }

    // Stamp the morph choreography signal on <html> of the NEW document (the
    // ::view-transition pseudo tree lives there). `data-vt-set` gates the
    // three-beat root choreography in CSS (old chrome fades out → hero morphs
    // → new chrome fades in); `data-vt-dir` allows per-direction tuning.
    // Must run inside pageswap/pagereveal, before the snapshot is captured.
    function stampChoreo(dir, set) {
        document.documentElement.dataset.vtDir = dir;
        if (set) document.documentElement.dataset.vtSet = set;
    }

    // Run cleanup after the transition finishes OR immediately if no transition
    // fired (browsers may dispatch pageswap/pagereveal for non-animated navs;
    // `e.viewTransition` is null in that case).
    function afterTransition(e, fn) {
        if (e.viewTransition && e.viewTransition.finished && typeof e.viewTransition.finished.finally === 'function') {
            e.viewTransition.finished.finally(fn);
        } else {
            fn();
        }
    }

    function bypassIfReducedMotion(e) {
        if (!reducedMotion.matches) return false;
        if (e.viewTransition && typeof e.viewTransition.skipTransition === 'function') {
            e.viewTransition.skipTransition();
        }
        return true;
    }

    // Pre-tag only when reduced-motion is NOT active. Under reduced motion we
    // skip the transition entirely (see `bypassIfReducedMotion`); writing
    // inline VT styles would just leave dead attributes in the DOM.
    if (!reducedMotion.matches) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', preTagAllCards);
        } else {
            preTagAllCards();
        }
    }

    // Safety net: a document can be bfcache-frozen after stripSiblingPreTags
    // ran but before the afterTransition re-tag (user navigated away
    // mid-transition). Restore the steady pre-tagged state on restore.
    window.addEventListener('pageshow', function (ev) {
        if (ev.persisted && !reducedMotion.matches) preTagAllCards();
    });

    /**
     * Match the clicked-outgoing card by its canonical detail URL. Plain
     * `a[href=...]` matching would mis-fire on in-card links (faculty chips,
     * department filters) that share or independently equal the activation
     * URL, falsely tagging the card even when the destination is unrelated.
     */
    function findOutgoingCard(targetURL) {
        if (!targetURL) return null;
        var escaped;
        try { escaped = CSS.escape(targetURL); } catch (_) { return null; }
        var card = document.querySelector('[data-post-id][data-vt-href="' + escaped + '"]');
        if (card) return card;
        // Fallback for legacy callers without `data-vt-href`: match the link
        // that's explicitly the post detail link. Existing markup conventions:
        //   a.stretched-link        — news-26, post-26
        //   a.text-reset.fw-bold    — post-featured's main article CTA
        // Avoid plain `a[href]` to keep department/chip links out.
        var candidates = document.querySelectorAll(
            '[data-post-id] a.stretched-link[href="' + escaped + '"],' +
            '[data-post-id] a.text-reset.fw-bold[href="' + escaped + '"]'
        );
        if (candidates.length === 1) return candidates[0].closest('[data-post-id]');
        return null;
    }

    window.addEventListener('pageswap', function (e) {
        if (bypassIfReducedMotion(e)) return;

        var targetURL = e.activation && e.activation.entry && e.activation.entry.url;
        var card = findOutgoingCard(targetURL);
        if (!card) return;

        stampChoreo(FORWARD, card.dataset.vtSet);
        // Stash postId AND instance so the back leg restores the correct
        // block when two listings on the same page share post IDs; stash the
        // set so the destination document can gate the root choreography.
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            postId: card.dataset.postId,
            instance: card.dataset.vtInstance || '',
            set: card.dataset.vtSet || '',
            scrollY: window.scrollY,
            url: location.pathname + location.search,
            targetURL: targetURL || ''
        }));
        tagCardForMorph(card, card.dataset.postId);

        afterTransition(e, function () {
            clearDynamic();
            clearDirAttrs();
        });
    });

    window.addEventListener('pagereveal', function (e) {
        var raw = sessionStorage.getItem(STORAGE_KEY);
        var stash = null;
        if (raw) {
            try { stash = JSON.parse(raw); } catch (_) { stash = null; }
        }

        // Forward-leg arrival: we are on the URL the stash targeted. Mark the
        // direction for detail-page CSS rules scoped to [data-vt-dir="forward"]
        // (see `_content-news.scss` hero lead-delay) and bail WITHOUT touching
        // the stash — the back leg still needs it. The previous `!hasCards`
        // gate was unreliable: detail pages can also contain `[data-post-id]`
        // cards (related-news section, card sliders), which made the script
        // fall through to the back-leg path and wipe the stash before the
        // user ever pressed back.
        if (stash && stash.targetURL && location.href === stash.targetURL) {
            stampChoreo(FORWARD, stash.set);
            stripSiblingPreTags(null);
            afterTransition(e, function () {
                clearDirAttrs();
                if (!reducedMotion.matches) preTagAllCards();
            });
            return;
        }

        // Back-leg arrival: we need listing cards on this page to morph into.
        if (!document.querySelector('[data-post-id]')) return;
        if (!stash || !stash.postId) {
            if (raw) sessionStorage.removeItem(STORAGE_KEY);
            return;
        }

        // Restore the card in the SAME block instance that initiated the
        // forward nav — query by both postId AND instance.
        var sel = stash.instance
            ? '[data-post-id="' + stash.postId + '"][data-vt-instance="' + CSS.escape(stash.instance) + '"]'
            : '[data-post-id="' + stash.postId + '"]';
        var card = document.querySelector(sel);
        if (!card) {
            // We're on a listing page but the specific card isn't here
            // (FacetWP load-more lost on hard back-nav, or a different
            // listing on a different page). No morph; root cross-fade plays.
            sessionStorage.removeItem(STORAGE_KEY);
            return;
        }

        // Restore scroll synchronously BEFORE the browser captures the new
        // snapshot, so the morph target is in-viewport at capture time.
        // Without this the snapshot is taken at scrollY=0 and the browser's
        // async auto-restore fires AFTER the transition, producing a "load
        // top then jump down" flash. Runs regardless of reduced-motion so
        // users who skip the animation still land in the right place. Gated
        // on url so we don't restore onto a different listing page.
        var currentURL = location.pathname + location.search;
        if (typeof stash.scrollY === 'number' && stash.url === currentURL) {
            window.scrollTo(0, stash.scrollY);
        }

        if (bypassIfReducedMotion(e)) {
            sessionStorage.removeItem(STORAGE_KEY);
            return;
        }

        stampChoreo(BACK, card.dataset.vtSet);
        tagCardForMorph(card, stash.postId);
        stripSiblingPreTags(card);

        afterTransition(e, function () {
            clearDynamic();
            clearDirAttrs();
            if (!reducedMotion.matches) preTagAllCards();
            sessionStorage.removeItem(STORAGE_KEY);
        });
    });
})();
